import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { ReadingProgressBar } from "@/components/site/ReadingProgressBar";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---------------- Glossary data ---------------- */

const GLOSSARY = [
  {
    id: "foundations",
    title: "Foundations",
    terms: [
      {
        n: 1,
        term: "Digital Asset",
        def: "Something of value that exists electronically and can be owned, transferred, or stored using digital systems. Stablecoins, cryptocurrencies, and tokenized assets are all types of digital assets.",
      },
      {
        n: 2,
        term: "Blockchain",
        def: "A distributed digital ledger that records transactions across a network of participants, rather than through a single central organization.",
      },
      {
        n: 3,
        term: "Cryptocurrency",
        def: "A digital asset that operates on blockchain technology without a central issuer. Bitcoin and Ethereum are common examples. Unlike stablecoins, their price can change significantly over short periods.",
      },
      {
        n: 4,
        term: "Transaction",
        def: "The transfer of a digital asset from one party to another. Each transaction creates a permanent record on the blockchain it was processed through.",
      },
      {
        n: 5,
        term: "Custodial vs. Non-Custodial Wallet",
        def: "A custodial wallet is one where a third party (such as an exchange or platform) holds and controls the private keys to a business's digital assets. A non-custodial wallet gives the business direct control instead. The distinction matters for accounting and risk: it determines who is actually responsible for safeguarding the asset.",
      },
    ],
  },
  {
    id: "how-stablecoins-work",
    title: "How Stablecoins Work",
    terms: [
      {
        n: 6,
        term: "Stablecoin",
        def: "A digital asset designed to maintain a relatively stable value, most often by linking it to a traditional currency such as the U.S. dollar.",
      },
      {
        n: 7,
        term: "Peg",
        def: "The reference value a stablecoin is designed to maintain — for example, 1 USDC ≈ 1 USD. When a stablecoin's market price moves away from this value, it is described as losing its peg, or depegging. TerraUSD's collapse in 2022 is the best-known example.",
      },
      {
        n: 8,
        term: "Reserve Assets",
        def: "The assets — such as cash, government securities, or other liquid holdings — that an issuer holds to support the value of a fiat-backed stablecoin.",
      },
      {
        n: 9,
        term: "Proof of Reserves (Attestation)",
        def: "A report or audit confirming that a stablecoin issuer actually holds the reserve assets it claims to hold. This is one of the main ways businesses assess whether a fiat-backed stablecoin is reliable.",
      },
      {
        n: 10,
        term: "Minting and Burning",
        def: "Minting is the creation of new stablecoin tokens, typically when a user deposits the underlying currency with the issuer. Burning is the removal of tokens from circulation, typically when they're redeemed. Together, they keep the token supply aligned with reserves.",
      },
      {
        n: 11,
        term: "Overcollateralized",
        def: "A model used by crypto-backed stablecoins, where the value of the collateral held exceeds the value of the tokens issued. This buffer helps absorb price swings in the underlying collateral.",
      },
      {
        n: 12,
        term: "Tokenization",
        def: "The process of representing an asset — such as a security, a fund, or even real estate — as a digital token on a blockchain. Stablecoins are one specific application of tokenization, applied to currency.",
      },
    ],
  },
  {
    id: "payments-and-infrastructure",
    title: "Payments & Infrastructure",
    terms: [
      {
        n: 13,
        term: "Payment Rail",
        def: "The infrastructure used to move money from one party to another. Banking networks are a traditional example; blockchain networks are a newer type of payment rail.",
      },
      {
        n: 14,
        term: "On-Chain vs. Off-Chain",
        def: "On-chain refers to data or activity recorded directly on a blockchain. Off-chain refers to information — like an invoice, an approval, or a contract — that exists outside the blockchain but is still necessary to understand a transaction. Most of the documentation finance teams need is off-chain.",
      },
      {
        n: 15,
        term: "Gas Fee (Network Fee)",
        def: "The fee paid to process a transaction on a blockchain network. Fees vary by network and congestion, and they typically need their own line in transaction records.",
      },
      {
        n: 16,
        term: "CBDC (Central Bank Digital Currency)",
        def: "A digital version of a national currency issued directly by a country's central bank, as part of the official monetary system — distinct from privately issued stablecoins.",
      },
    ],
  },
  {
    id: "accounting-and-tax",
    title: "Accounting & Tax",
    terms: [
      {
        n: 17,
        term: "Disposition",
        def: "Any event where a business gives up or transfers a digital asset — including a sale, an exchange, or a payment. Many tax authorities treat a disposition as a taxable event, even when the asset's value barely changed.",
      },
      {
        n: 18,
        term: "Cost Basis",
        def: "The value of a digital asset at the time it was acquired, used to calculate a gain or loss when it's later disposed of. Because stablecoin activity often involves many small transactions, tracking cost basis accurately can require more granular records than traditional payments do.",
      },
      {
        n: 19,
        term: "Redemption",
        def: "Exchanging a stablecoin back for the currency it's linked to, typically at face value. Whether this counts as a taxable event depends on the jurisdiction and the type of stablecoin involved.",
      },
      {
        n: 20,
        term: "EMT (Electronic Money Token)",
        def: "A category defined under the EU's MiCA regulation for stablecoins linked to a single official currency, such as the euro. EMTs are subject to specific reserve and consumer-protection requirements and, in some countries, receive different tax treatment than other crypto-assets.",
      },
      {
        n: 21,
        term: "Digital Asset Broker",
        def: "A platform or service that facilitates digital asset transactions and, in some jurisdictions, is required to report transaction data to tax authorities. In the U.S., for example, brokers began reporting proceeds via Form 1099-DA starting in 2026.",
      },
    ],
  },
  {
    id: "smart-contracts-and-compliance",
    title: "Smart Contracts & Compliance",
    terms: [
      {
        n: 22,
        term: "Smart Contract",
        def: "A program that automatically executes predefined actions when specific conditions are met. Some blockchain-based financial services use smart contracts to automate payments or approvals.",
      },
      {
        n: 23,
        term: "MSB (Money Services Business)",
        def: "A regulatory designation for companies that provide money transmission or currency exchange services. Stablecoin issuers and payment platforms are often required to register as an MSB, which brings additional compliance obligations.",
      },
      {
        n: 24,
        term: "VASP (Virtual Asset Service Provider)",
        def: "A term used by international regulators (including the Financial Action Task Force) to describe businesses that provide services involving digital assets, such as exchanges or custody providers. VASP status typically triggers specific AML and reporting requirements.",
      },
      {
        n: 25,
        term: "Travel Rule",
        def: "A compliance requirement that obligates financial institutions and, increasingly, digital asset service providers to share sender and recipient information for transactions above a certain threshold. It exists to prevent money laundering and applies differently to blockchain-based transfers than it does to traditional wire transfers.",
      },
    ],
  },
];

/* ---------------- Hero ---------------- */

const ArticleHero = () => (
  <section data-testid="article-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="article-breadcrumb">
        <Link
          to="/resources"
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid="article-breadcrumb-resources-link"
        >
          Resources
        </Link>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        <span className="text-foreground/80">Glossary: 25 Essential Digital Finance Terms</span>
      </nav>

      {/* Hero panel */}
      <div
        className="mt-10 rounded-3xl bg-[hsl(var(--primary))]/[0.05] px-[14px] py-[33px] sm:px-[38px] sm:py-[49px] lg:px-[54px] lg:py-[65px]"
        data-testid="article-hero-panel"
      >
        <h1 className="font-serif-editorial max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Glossary: 25 Essential Digital Finance Terms
        </h1>
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="flex items-center gap-2.5">
            <span className="h-[3px] w-5 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(174_86%_24%)]">
              Glossary
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            5 min read
          </span>
        </div>
        <div className="mt-8 max-w-4xl lg:max-w-[1080px]">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            Digital finance introduces new terminology that finance
            professionals, accountants, and business leaders are increasingly
            encountering in conversations about payments, accounting, and
            financial operations. This glossary explains 25 of the most
            essential terms in simple language, with a focus on the concepts
            specific to stablecoins and digital assets, and provides context
            for how each one relates to business finance.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="article-hero-tags">
          {["Digital Assets", "Accounting", "Stablecoins", "Payments"].map((t) => (
            <span
              key={t}
              className="rounded-full border border-[hsl(var(--primary))]/25 px-4 py-1.5 text-sm font-medium text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Sticky table of contents with scrollspy ---------------- */

const TocSidebar = () => {
  const [activeId, setActiveId] = useState(GLOSSARY[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current = GLOSSARY[0].id;
      for (const item of GLOSSARY) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <aside className="hidden lg:block">
      <nav
        className="sticky top-24 rounded-2xl bg-card/80 p-7 px-[23px] shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px]"
        aria-label="Table of contents"
        data-testid="article-toc"
      >
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Table of Contents
        </h2>
        <ol className="mt-5 space-y-1">
          {GLOSSARY.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={`block rounded-md py-1.5 pr-2 text-sm leading-snug transition-colors hover:text-[hsl(174_86%_24%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  activeId === s.id
                    ? "font-medium text-[hsl(174_86%_24%)]"
                    : "text-muted-foreground"
                }`}
                data-testid={`article-toc-link-${s.id}`}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  );
};

/* ---------------- Glossary body ---------------- */

const ArticleBody = () => (
  <section data-testid="article-body-section">
    <Container>
      <div className="grid gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">
        <TocSidebar />

        <article className="max-w-[76ch]" data-testid="article-content">
          {GLOSSARY.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="scroll-mt-28 pt-14 first:pt-0"
              data-testid={`article-section-${section.id}`}
            >
              <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.7rem]">
                {section.title}
              </h2>
              <div className="mt-6 space-y-8">
                {section.terms.map((t) => (
                  <div
                    key={t.n}
                    className="border-t border-[hsl(var(--accent))]/60 pt-5"
                    data-testid={`glossary-term-${t.n}`}
                  >
                    <div className="flex items-baseline gap-4">
                      <span className="text-sm font-medium tracking-[0.08em] text-[hsl(174_86%_24%)]">
                        {String(t.n).padStart(2, "0")}
                      </span>
                      <h3 className="text-lg font-semibold tracking-[-0.01em] text-foreground">
                        {t.term}
                      </h3>
                    </div>
                    <p className="mt-3 text-base leading-[1.75] text-muted-foreground">
                      {t.def}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </article>
      </div>
    </Container>
  </section>
);

/* ---------------- Final CTA ---------------- */

const ArticleFinalCta = () => (
  <section data-testid="article-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Continue Building Your Expertise
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              Move from fundamental concepts to structured learning with WFII
              programs designed for finance professionals working with digital
              money, payments, accounting workflows, and compliance.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="article-final-cta-explore-programs-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Page ---------------- */

export default function ArticleGlossaryPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <ReadingProgressBar />
        <main className="wfii-longform flex-1 space-y-[60px] lg:space-y-[100px]">
          <ArticleHero />
          <ArticleBody />
          <ArticleFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
